import React, { useEffect, useRef } from "react";
import * as THREE from "three";

class TouchTexture {
  constructor(size = 64) {
    this.size = size;
    this.maxAge = 64;
    this.radius = 0.22 * this.size;
    this.speed = 1 / this.maxAge;
    this.trail = [];
    this.last = null;

    this.canvas = document.createElement("canvas");
    this.canvas.width = this.size;
    this.canvas.height = this.size;
    this.ctx = this.canvas.getContext("2d");
    this.texture = new THREE.Texture(this.canvas);
    this.clear();
  }

  clear() {
    this.ctx.fillStyle = "black";
    this.ctx.fillRect(0, 0, this.size, this.size);
  }

  addTouch(point) {
    let force = 0;
    let vx = 0;
    let vy = 0;
    if (this.last) {
      const dx = point.x - this.last.x;
      const dy = point.y - this.last.y;
      if (dx === 0 && dy === 0) return;
      const dd = dx * dx + dy * dy;
      const d = Math.sqrt(dd);
      vx = dx / d;
      vy = dy / d;
      force = Math.min(dd * 16000, 1.8);
    }
    this.last = { x: point.x, y: point.y };
    this.trail.push({ x: point.x, y: point.y, age: 0, force, vx, vy });
  }

  drawPoint(point) {
    const pos = {
      x: point.x * this.size,
      y: (1 - point.y) * this.size,
    };

    let intensity = 1;
    if (point.age < this.maxAge * 0.3) {
      intensity = Math.sin((point.age / (this.maxAge * 0.3)) * (Math.PI / 2));
    } else {
      const t = 1 - (point.age - this.maxAge * 0.3) / (this.maxAge * 0.7);
      intensity = -t * (t - 2);
    }
    intensity *= point.force;

    const radius = this.radius;
    const color = `${((point.vx + 1) / 2) * 255},${((point.vy + 1) / 2) * 255},${intensity * 255}`;
    const offset = this.size * 4;

    this.ctx.shadowOffsetX = offset;
    this.ctx.shadowOffsetY = offset;
    this.ctx.shadowBlur = radius;
    this.ctx.shadowColor = `rgba(${color},${0.24 * intensity})`;

    this.ctx.beginPath();
    this.ctx.fillStyle = "rgba(255,0,0,1)";
    this.ctx.arc(pos.x - offset, pos.y - offset, radius, 0, Math.PI * 2);
    this.ctx.fill();
  }

  update() {
    this.clear();
    const speed = this.speed;
    for (let i = this.trail.length - 1; i >= 0; i -= 1) {
      const point = this.trail[i];
      const f = point.force * speed * (1 - point.age / this.maxAge);
      point.x += point.vx * f;
      point.y += point.vy * f;
      point.age += 1;
      if (point.age > this.maxAge) {
        this.trail.splice(i, 1);
      } else {
        this.drawPoint(point);
      }
    }
    this.texture.needsUpdate = true;
  }
}

const LiquidBackground = ({ className }) => {
  const rootRef = useRef(null);

  useEffect(() => {
    const mount = rootRef.current;
    if (!mount) return undefined;

    let frameId = null;
    const clock = new THREE.Clock();
    const touchTexture = new TouchTexture();

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      powerPreference: "high-performance",
      stencil: false,
      depth: false,
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    mount.appendChild(renderer.domElement);

    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    const uniforms = {
      uTime: { value: 0 },
      uResolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
      uTouchTexture: { value: touchTexture.texture },
      uColor1: { value: new THREE.Vector3(0.72, 0.34, 0.18) },
      uColor2: { value: new THREE.Vector3(0.1, 0.09, 0.34) },
      uColor3: { value: new THREE.Vector3(0.14, 0.46, 0.72) },
      uDarkNavy: { value: new THREE.Vector3(0.018, 0.018, 0.032) },
    };

    const material = new THREE.ShaderMaterial({
      uniforms,
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position.xy, 0.0, 1.0);
        }
      `,
      fragmentShader: `
        uniform float uTime;
        uniform vec2 uResolution;
        uniform sampler2D uTouchTexture;
        uniform vec3 uColor1;
        uniform vec3 uColor2;
        uniform vec3 uColor3;
        uniform vec3 uDarkNavy;
        varying vec2 vUv;

        float noise(vec2 p) {
          return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
        }

        // Layered grain to get a gritty sand/paper texture
        float grain(vec2 uv, float t) {
          vec2 p1 = uv * uResolution * 0.62;
          vec2 p2 = uv * uResolution * 1.15;
          vec2 p3 = uv * uResolution * 2.4;

          float g1 = noise(p1 + t * 0.8) * 2.0 - 1.0;
          float g2 = noise(p2 - t * 0.45) * 2.0 - 1.0;
          float g3 = noise(p3 + vec2(t * 0.25, -t * 0.18)) * 2.0 - 1.0;

          // Weighted blend: visible grit without turning into TV static
          return g1 * 0.42 + g2 * 0.34 + g3 * 0.24;
        }

        void main() {
          vec2 uv = vUv;
          vec4 touch = texture2D(uTouchTexture, uv);
          float vx = -(touch.r * 2.0 - 1.0);
          float vy = -(touch.g * 2.0 - 1.0);
          float intensity = touch.b;

          uv += vec2(vx, vy) * (0.62 * intensity);

          vec2 p = uv - 0.5;
          float t = uTime * 0.8;
          float wave = sin(length(p) * 14.0 - t * 2.2) * 0.034;
          uv += vec2(wave);

          vec2 c1 = vec2(0.5 + sin(t * 0.5) * 0.38, 0.5 + cos(t * 0.6) * 0.42);
          vec2 c2 = vec2(0.5 + cos(t * 0.65) * 0.42, 0.5 + sin(t * 0.55) * 0.36);
          vec2 c3 = vec2(0.5 + sin(t * 0.72) * 0.35, 0.5 + cos(t * 0.48) * 0.33);

          float d1 = length(uv - c1);
          float d2 = length(uv - c2);
          float d3 = length(uv - c3);

          float i1 = 1.0 - smoothstep(0.0, 0.44, d1);
          float i2 = 1.0 - smoothstep(0.0, 0.45, d2);
          float i3 = 1.0 - smoothstep(0.0, 0.41, d3);

          vec3 color = vec3(0.0);
          color += uColor1 * i1 * (0.55 + 0.45 * sin(t * 1.2));
          color += uColor2 * i2 * (0.6 + 0.4 * cos(t * 1.4));
          color += uColor3 * i3 * (0.5 + 0.5 * sin(t * 1.6));

          float radial = 1.0 - smoothstep(0.0, 0.9, length(uv - 0.5));
          color += mix(uColor2, uColor1, radial) * 0.3;

          float lum = dot(color, vec3(0.299, 0.587, 0.114));
          color = mix(vec3(lum), color, 1.28);
          color = pow(color, vec3(0.93));

          float b = length(color);
          color = mix(uDarkNavy, color, max(b * 1.06, 0.065));

          // Paper-like micro texture (gritty but controlled)
          float g = grain(uv, uTime * 0.55);
          color += g * 0.1;

          // Slight luma-dependent grain keeps it organic (like filmed paper)
          float luma = dot(color, vec3(0.299, 0.587, 0.114));
          color += vec3(g * (0.062 + (1.0 - luma) * 0.055));
          color = clamp(color, vec3(0.0), vec3(1.0));

          gl_FragColor = vec4(color, 1.0);
        }
      `,
    });

    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
    scene.add(quad);

    const onResize = () => {
      renderer.setSize(window.innerWidth, window.innerHeight);
      uniforms.uResolution.value.set(window.innerWidth, window.innerHeight);
    };

    const onPointerMove = (x, y) => {
      touchTexture.addTouch({
        x: x / window.innerWidth,
        y: 1 - y / window.innerHeight,
      });
    };

    const handleMouseMove = (ev) => onPointerMove(ev.clientX, ev.clientY);
    const handleTouchMove = (ev) => {
      if (!ev.touches || !ev.touches[0]) return;
      onPointerMove(ev.touches[0].clientX, ev.touches[0].clientY);
    };

    window.addEventListener("resize", onResize);
    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: true });

    const tick = () => {
      const delta = Math.min(clock.getDelta(), 0.1);
      uniforms.uTime.value += delta;
      touchTexture.update();
      renderer.render(scene, camera);
      frameId = window.requestAnimationFrame(tick);
    };
    tick();

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("touchmove", handleTouchMove);
      scene.remove(quad);
      quad.geometry.dispose();
      material.dispose();
      touchTexture.texture.dispose();
      renderer.dispose();
      if (mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  return <div ref={rootRef} className={className} aria-hidden="true" />;
};

export default LiquidBackground;
